export const validateCoordinate = (value: number, type: "lat" | "lng"): number => {
  if (isNaN(value) || !isFinite(value)) {
    return 0;
  }

  if (type === "lat") {
    return Math.max(-90, Math.min(90, value));
  } else {
    return Math.max(-180, Math.min(180, value));
  }
};

export const calculateHaversineDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  const rounded = Math.round(distance * 1000) / 1000;
  return rounded;
};

export const calculateTotalRange = (fuelLevel: number, fuelConsumption: number): number => {
  if (fuelLevel <= 0 || fuelConsumption <= 0) {
    return 0;
  }
  return fuelLevel * fuelConsumption;
};

export const calculateSegmentDistance = (points: Array<{ lat: number; lng: number }>): number => {
  if (!points || points.length < 2) {
    return 0;
  }

  let totalDistance = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const point1 = points[i];
    const point2 = points[i + 1];

    const lat1 = validateCoordinate(Number(point1.lat), "lat");
    const lng1 = validateCoordinate(Number(point1.lng), "lng");
    const lat2 = validateCoordinate(Number(point2.lat), "lat");
    const lng2 = validateCoordinate(Number(point2.lng), "lng");

    if (lat1 !== 0 && lng1 !== 0 && lat2 !== 0 && lng2 !== 0) {
      totalDistance += calculateHaversineDistance(lat1, lng1, lat2, lng2);
    }
  }

  return totalDistance;
};

export const createPartialSegment = (
  points: Array<{ lat: number; lng: number }>,
  maxDistance: number
): Array<{ lat: number; lng: number }> => {
  if (!points || points.length < 2 || maxDistance <= 0) {
    return [];
  }

  const partialPoints: Array<{ lat: number; lng: number }> = [];
  let accumulatedDistance = 0;

  partialPoints.push({
    lat: validateCoordinate(Number(points[0].lat), "lat"),
    lng: validateCoordinate(Number(points[0].lng), "lng"),
  });

  for (let i = 0; i < points.length - 1; i++) {
    const point1 = points[i];
    const point2 = points[i + 1];

    const lat1 = validateCoordinate(Number(point1.lat), "lat");
    const lng1 = validateCoordinate(Number(point1.lng), "lng");
    const lat2 = validateCoordinate(Number(point2.lat), "lat");
    const lng2 = validateCoordinate(Number(point2.lng), "lng");

    if (lat1 === 0 || lng1 === 0 || lat2 === 0 || lng2 === 0) {
      continue;
    }

    const segmentDistance = calculateHaversineDistance(lat1, lng1, lat2, lng2);

    if (accumulatedDistance + segmentDistance <= maxDistance) {
      accumulatedDistance += segmentDistance;
      partialPoints.push({
        lat: lat2,
        lng: lng2,
      });
    } else {
      const remainingDistance = maxDistance - accumulatedDistance;
      const ratio = remainingDistance / segmentDistance;

      if (ratio > 0 && ratio <= 1) {
        const partialLat = lat1 + (lat2 - lat1) * ratio;
        const partialLng = lng1 + (lng2 - lng1) * ratio;

        partialPoints.push({
          lat: validateCoordinate(partialLat, "lat"),
          lng: validateCoordinate(partialLng, "lng"),
        });
      }
      break;
    }
  }

  return partialPoints;
};

/**
 * Dados de autonomia para debug e informações
 */
export interface AutonomyData {
  isEnabled: boolean;
  maxRangeKm: number;
  actualRangeKm: number;
  fuelLevel: number;
  consumption: number;
}

/**
 * Estrutura de um segmento de autonomia
 */
export interface AutonomySegment {
  points: Array<{ lat: number; lng: number }>;
  alternative: boolean;
  showArrows: boolean;
  data: {
    autonomyRange: boolean;
    maxRange: number;
    actualRange: number;
  };
}

const findClosestPointOnRoute = (
  segmentPoints: any[],
  stopLat: number,
  stopLng: number
): { minDistance: number; bestPosition: number } => {
  let minDistance = Infinity;
  let bestPosition = 0;
  let accumulatedDistance = 0;

  for (let i = 0; i < segmentPoints.length - 1; i++) {
    const routePoint = segmentPoints[i];
    const distance = calculateHaversineDistance(
      validateCoordinate(Number(stopLat), "lat"),
      validateCoordinate(Number(stopLng), "lng"),
      validateCoordinate(Number(routePoint.lat), "lat"),
      validateCoordinate(Number(routePoint.lng), "lng")
    );

    if (distance < minDistance) {
      minDistance = distance;
      bestPosition = accumulatedDistance;
    }

    if (i < segmentPoints.length - 1) {
      const nextPoint = segmentPoints[i + 1];
      accumulatedDistance += calculateHaversineDistance(
        validateCoordinate(Number(routePoint.lat), "lat"),
        validateCoordinate(Number(routePoint.lng), "lng"),
        validateCoordinate(Number(nextPoint.lat), "lat"),
        validateCoordinate(Number(nextPoint.lng), "lng")
      );
    }
  }

  return { minDistance, bestPosition };
};

const findRefuelingPositions = (
  segmentPoints: any[],
  refuelingStops: any[]
): Array<{ distanceFromStart: number; stop: any }> => {
  const refuelingPositions = [];

  for (const stop of refuelingStops) {
    const stopLat = stop.lat || stop.position?.lat;
    const stopLng = stop.lng || stop.position?.lng;

    if (stopLat && stopLng) {
      const { minDistance, bestPosition } = findClosestPointOnRoute(
        segmentPoints,
        stopLat,
        stopLng
      );

      if (minDistance <= 1) {
        refuelingPositions.push({
          distanceFromStart: bestPosition,
          stop,
        });
      }
    }
  }

  const sortedPositions = refuelingPositions.sort((a, b) => a.distanceFromStart - b.distanceFromStart);

  return sortedPositions;
};

/**
 * Cria um segmento de autonomia a partir de uma distância específica na rota
 */
const createAutonomySegmentFromStart = (
  segmentPoints: any[],
  startDistance: number,
  range: number,
  totalRouteDistance: number
): AutonomySegment | null => {
  if (!segmentPoints || segmentPoints.length < 2) {
    return null;
  }

  let startPointIndex = 0;
  let accumulatedDistance = 0;

  if (startDistance > 0) {
    for (let i = 0; i < segmentPoints.length - 1; i++) {
      const point1 = segmentPoints[i];
      const point2 = segmentPoints[i + 1];

      const segmentDistance = calculateHaversineDistance(
        Number(point1.lat),
        Number(point1.lng),
        Number(point2.lat),
        Number(point2.lng)
      );

      if (accumulatedDistance + segmentDistance >= startDistance) {
        startPointIndex = i;
        break;
      }

      accumulatedDistance += segmentDistance;
      startPointIndex = i + 1;
    }
  }

  if (startPointIndex >= segmentPoints.length) {
    return null;
  }

  const endDistance = startDistance + range;
  const segmentRangePoints = [];

  segmentRangePoints.push(segmentPoints[startPointIndex]);

  accumulatedDistance = startDistance;

  for (let i = startPointIndex; i < segmentPoints.length - 1; i++) {
    const point1 = segmentPoints[i];
    const point2 = segmentPoints[i + 1];

    const segmentDistance = calculateHaversineDistance(
      Number(point1.lat),
      Number(point1.lng),
      Number(point2.lat),
      Number(point2.lng)
    );

    accumulatedDistance += segmentDistance;

    if (accumulatedDistance <= endDistance) {
      segmentRangePoints.push(point2);
    } else {
      const remainingDistance = endDistance - (accumulatedDistance - segmentDistance);
      const ratio = remainingDistance / segmentDistance;

      const interpolatedLat = Number(point1.lat) + (Number(point2.lat) - Number(point1.lat)) * ratio;
      const interpolatedLng = Number(point1.lng) + (Number(point2.lng) - Number(point1.lng)) * ratio;

      segmentRangePoints.push({
        lat: interpolatedLat,
        lng: interpolatedLng,
      });
      break;
    }
  }

  if (segmentRangePoints.length < 2) {
    return null;
  }

  return {
    points: segmentRangePoints.map((point: any) => ({
      lat: validateCoordinate(Number(point.lat), "lat"),
      lng: validateCoordinate(Number(point.lng), "lng"),
    })),
    alternative: false,
    showArrows: false,
    data: {
      autonomyRange: true,
      maxRange: range,
      actualRange: calculateSegmentDistance(segmentRangePoints),
    },
  };
};

/**
 * Calcula os segmentos de autonomia baseado nos segmentos da rota
 * Cria múltiplos segmentos verdes: um inicial + um para cada parada de reabastecimento
 * @param segments Segmentos da rota original
 * @param enableAutonomy Se a autonomia está habilitada
 * @param fuelLevel Nível de combustível inicial
 * @param fuelConsumption Consumo de combustível (km/litro)
 * @param tankCapacity Capacidade total do tanque
 * @param refuelingStops Paradas de reabastecimento do via
 * @returns Array de segmentos de autonomia (múltiplos segmentos verdes)
 */
export const calculateAutonomySegments = (
  segments: any[],
  enableAutonomy: boolean,
  fuelLevel: number,
  fuelConsumption: number,
  tankCapacity: number = 0,
  refuelingStops: any[] = []
): { segments: AutonomySegment[]; data: AutonomyData } => {
  const autonomyData: AutonomyData = {
    isEnabled: enableAutonomy,
    maxRangeKm: calculateTotalRange(fuelLevel, fuelConsumption),
    actualRangeKm: 0,
    fuelLevel,
    consumption: fuelConsumption,
  };

  if (!enableAutonomy || !segments?.length || !segments[0]?.points?.length || fuelLevel <= 0 || fuelConsumption <= 0) {
    return { segments: [], data: autonomyData };
  }

  const segmentPoints = segments[0].points;
  const totalRouteDistance = calculateSegmentDistance(segmentPoints);
  const autonomySegments: AutonomySegment[] = [];

  const fullTankRange = tankCapacity > 0 ? calculateTotalRange(tankCapacity, fuelConsumption) : autonomyData.maxRangeKm;

  // Segmento inicial
  const initialSegment = createAutonomySegmentFromStart(segmentPoints, 0, autonomyData.maxRangeKm, totalRouteDistance);
  if (initialSegment) {
    autonomySegments.push(initialSegment);
    autonomyData.actualRangeKm += initialSegment.data.actualRange;
  }

  // Segmentos de reabastecimento
  if (refuelingStops.length > 0 && tankCapacity > 0) {
    const refuelingPositions = findRefuelingPositions(segmentPoints, refuelingStops);

    for (const refuelingPos of refuelingPositions) {
      const startDistance = refuelingPos.distanceFromStart;
      const refuelingSegment = createAutonomySegmentFromStart(
        segmentPoints,
        startDistance,
        fullTankRange,
        totalRouteDistance
      );

      if (refuelingSegment) {
        autonomySegments.push(refuelingSegment);
        autonomyData.actualRangeKm += refuelingSegment.data.actualRange;
      }
    }
  }

  return { segments: autonomySegments, data: autonomyData };
};
